import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Droplets } from "lucide-react";
import { getSuburb, SUBURBS } from "../lib/suburbs";
import { useSEO } from "../lib/seo";
import { BUSINESS } from "../lib/constants";
import { Reveal, Overline } from "../components/Reveal";
import { CTABand } from "../components/CTABand";

const FEATURED_SERVICES = [
  { label: "Carpet Cleaning", to: "/carpet-cleaning-brisbane" },
  { label: "Couch & Lounge Cleaning", to: "/couch-cleaning-brisbane" },
  { label: "Rug Cleaning", to: "/rug-cleaning-brisbane" },
  { label: "Mattress Cleaning", to: "/mattress-cleaning-brisbane" },
  { label: "Tile & Grout Cleaning", to: "/tile-grout-cleaning-brisbane" },
  { label: "Pet Stain & Odour Cleaning", to: "/pet-stain-odour-cleaning-brisbane" },
];

const SuburbPage = () => {
  const { slug } = useParams();
  const suburb = getSuburb(slug);
  useSEO(
    suburb ? `Carpet Cleaning ${suburb.name} | Kay's Carpet Cleaning Gold Coast` : "Kay's Carpet Cleaning",
    suburb ? `Professional carpet, couch, rug, mattress and tile & grout cleaning in ${suburb.name}, Gold Coast. 7+ years of experience. Call Kay's Carpet Cleaning on 0450 311 907 for a free estimate.` : ""
  );
  if (!suburb) return <Navigate to="/service-areas" replace />;

  const nearby = suburb.nearby
    .map((s) => SUBURBS.find((x) => x.slug === s))
    .filter(Boolean);

  return (
    <div data-testid={`suburb-page-${slug}`}>
      <section className="relative overflow-hidden">
        <div aria-hidden="true" className="absolute -top-24 -right-32 w-[30rem] h-[30rem] rounded-full bg-brand-mist/70 blur-3xl" />
        <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-14 pb-10 sm:pt-20 sm:pb-14 relative">
          <Reveal>
            <Overline>{suburb.region} · Service Area</Overline>
            <h1 className="mt-4 font-display font-medium text-4xl sm:text-5xl lg:text-6xl leading-[1.06] tracking-tight text-brand-ink" data-testid="suburb-h1">
              Carpet & Cleaning Services in <span className="font-serif italic text-brand">{suburb.name}</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-brand-muted leading-relaxed">{suburb.intro}</p>
          </Reveal>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-5 sm:px-8 pb-8">
        <Reveal>
          <h2 className="font-display font-medium text-3xl sm:text-4xl tracking-tight text-brand-ink">
            What we clean in {suburb.name}
          </h2>
          <p className="mt-5 text-base sm:text-lg text-brand-muted leading-relaxed">{suburb.body}</p>
          <ul className="mt-6 space-y-3">
            {suburb.points.map((p) => (
              <li key={p} className="flex items-start gap-3 text-base text-brand-muted leading-relaxed">
                <CheckCircle2 size={18} className="text-brand shrink-0 mt-1" />
                {p}
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal className="mt-12">
          <div className="grid sm:grid-cols-2 gap-3" data-testid="suburb-services">
            {FEATURED_SERVICES.map((s) => (
              <Link
                key={s.to}
                to={s.to}
                data-testid={`suburb-service-${s.to.slice(1)}`}
                className="group flex items-center justify-between gap-4 bg-white border border-brand-line/60 rounded-xl px-5 py-4 hover:border-brand transition-colors duration-300"
              >
                <span className="text-sm font-semibold text-brand-ink group-hover:text-brand transition-colors duration-300">{s.label}</span>
                <ArrowRight size={16} className="text-brand-muted group-hover:text-brand group-hover:translate-x-1 transition-[color,transform] duration-300" />
              </Link>
            ))}
          </div>
        </Reveal>

        <Reveal className="mt-8">
          <Link
            to="/water-flood-damage-restoration-brisbane"
            data-testid="suburb-water-link"
            className="flex items-center justify-between gap-4 bg-brand-dark text-white rounded-2xl px-6 py-5 hover:bg-brand transition-colors duration-300"
          >
            <span className="flex items-center gap-3 text-sm sm:text-base font-semibold">
              <Droplets size={18} className="text-[#A7E3DC] shrink-0" />
              Water damage in {suburb.name}? Call {BUSINESS.phoneDisplay}
            </span>
            <ArrowRight size={17} className="shrink-0" />
          </Link>
        </Reveal>

        <div className="mt-14">
          <CTABand id="suburb-cta" heading={`Get a Cleaning Estimate in ${suburb.name}`} text={`Tell us what you need cleaned in ${suburb.name} and we'll provide an estimate based on your job — free and no-obligation.`} primaryLabel="Get My Estimate" />
        </div>

        {nearby.length > 0 && (
          <Reveal className="mt-14">
            <h2 className="font-display font-medium text-2xl tracking-tight text-brand-ink">Nearby areas we service</h2>
            <div className="mt-5 flex flex-wrap gap-2.5">
              {nearby.map((n) => (
                <Link
                  key={n.slug}
                  to={`/service-areas/${n.slug}`}
                  data-testid={`suburb-nearby-${n.slug}`}
                  className="text-sm font-medium text-brand bg-brand-mist/70 border border-brand-line/60 px-4 py-2 rounded-full hover:bg-brand-mist transition-colors duration-300"
                >
                  {n.name}
                </Link>
              ))}
              <Link
                to="/service-areas"
                data-testid="suburb-all-areas-link"
                className="text-sm font-semibold text-brand-muted px-4 py-2 rounded-full border border-brand-line hover:text-brand hover:border-brand transition-colors duration-300"
              >
                All service areas
              </Link>
            </div>
          </Reveal>
        )}
      </div>
    </div>
  );
};

export default SuburbPage;
